import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Lazy load pages for better initial load time
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const SupermarketFloorPlan = lazy(() => import("./pages/SupermarketFloorPlan"));
const LeasesPage = lazy(() => import("./pages/LeasesPage"));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const AIAnalyzerPage = lazy(() => import("./pages/AIAnalyzerPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const CommercialAgreementPage = lazy(() => import("./pages/CommercialAgreementPage"));
const DataSyncPage = lazy(() => import("./pages/DataSyncPage"));

// Loading spinner for lazy loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Role-aware default redirect
const DefaultRedirect = () => {
  const { user } = useAuth();
  const role = user?.role;
  if (role === 'viewer' || role === 'chef_rayon') {
    return <Navigate to="/floor-plan" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="simlabdt-theme">
      <AuthProvider>
        <BrowserRouter>
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <DashboardPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/floor-plan" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <SupermarketFloorPlan />
              </Suspense>
            </Layout>
          } />
          <Route path="/leases" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <LeasesPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/invoices" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <InvoicesPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/clients" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <ClientsPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/documents" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <DocumentsPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/notifications" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <NotificationsPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/ai-analyzer" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AIAnalyzerPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/admin" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <AdminPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/admin/data-sync" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <DataSyncPage />
              </Suspense>
            </Layout>
          } />
          <Route path="/commercial-agreement" element={
            <Layout>
              <Suspense fallback={<PageLoader />}>
                <CommercialAgreementPage />
              </Suspense>
            </Layout>
          } />
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
